/**
 * 관리자 설정의 사이트(이름 · 대표 이미지)와 NAS 연결 상태 — backend `app/site_settings/router.py`.
 *
 * 사이트는 `/site/` 와 같은 원천(db.site)을 고친다 — 사이드바와 로그인 화면이 바로 따라온다.
 * 대표 이미지는 NAS 대신 data URL 로 들고 있는다. NAS 는 늘 연결된 것으로 답한다.
 */
import { HttpResponse, http } from "msw";
import type { NasConnection, SiteProfile } from "@/types";
import { site } from "../data/site";
import { db } from "../db";
import { fail, latency, requireAdmin } from "../http";

const BASE = "/api/v1/admin";
const MAX_ICON_BYTES = 2 * 1024 * 1024;
/** 서버처럼 SVG 는 받지 않는다. */
const ICON_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
const GB = 1024 ** 3;

const profile = (): SiteProfile => ({
  display_name: db.site.display_name,
  icon_url: db.site.icon_url,
});

const dataUrl = async (file: File) => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return `data:${file.type};base64,${btoa(binary)}`;
};

export const officeHandlers = [
  http.get(`${BASE}/site-profile/`, async () => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    return HttpResponse.json(profile());
  }),

  /** 비우면 배포 설정의 이름(시드의 이름)으로 돌아간다. */
  http.patch(`${BASE}/site-profile/`, async ({ request }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const { display_name: name } = (await request.json()) as { display_name: string };
    if (name.length > 40) return fail(422, "사이트 이름은 40자까지 적을 수 있습니다.");
    db.site.display_name = name.trim() || site.display_name;
    return HttpResponse.json(profile());
  }),

  http.post(`${BASE}/site-profile/icon/`, async ({ request }) => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const file = (await request.formData()).get("file");
    if (!(file instanceof File) || !ICON_TYPES.includes(file.type))
      return fail(422, "이미지 파일만 올릴 수 있습니다.");
    if (file.size > MAX_ICON_BYTES) return fail(422, "대표 이미지는 2MB까지 올릴 수 있습니다.");
    db.site.icon_url = await dataUrl(file);
    return HttpResponse.json(profile());
  }),

  http.delete(`${BASE}/site-profile/icon/`, async () => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    db.site.icon_url = null;
    return HttpResponse.json(profile());
  }),

  http.get(`${BASE}/nas/`, async () => {
    await latency();
    const user = requireAdmin();
    if (user instanceof Response) return user;
    const nas: NasConnection = {
      status: "connected",
      total_bytes: 4000 * GB,
      free_bytes: 2650 * GB,
      checked_at: new Date().toISOString(),
    };
    return HttpResponse.json(nas);
  }),
];
